import { useCallback, useMemo, type ComponentType } from "react";
import { useNavigate } from "react-router";
import { useSession } from "../../app/session";
import { useViewCtx } from "../../app/useViewCtx";
import { useWorkspaceDocs } from "../../core/useWorkspaceDocs";
import type { DocHandle } from "../../sync/docManager";
import type { ViewCtx, ViewProps } from "../../registry/viewTypes";
import type { Source } from "./source";

/**
 * "Show from" (ADR 0024): a widget can show, below its own workspace's rows, the rows of the person's other
 * workspaces — all of them, or only what is theirs there (`for: me`: their turn, assigned to them, or to
 * nobody). Each other workspace is the same widget over that workspace's doc, under its name; a workspace with
 * nothing to show is left out. Ticking a row off writes to its own workspace; opening a task goes there.
 */
export function Elsewhere<Cfg extends { source?: Source }>({
  View,
  props,
  mode,
}: {
  View: ComponentType<ViewProps<Cfg>>;
  props: ViewProps<Cfg>;
  mode: "mine" | "all";
}) {
  const workspaces = useSession((s) => s.session?.workspaces) ?? [];
  const others = workspaces.filter((w) => w.id !== props.ctx.workspaceId);
  const handles = useWorkspaceDocs(others.map((w) => w.id));
  return (
    <>
      {others.map((w) =>
        handles[w.id] ? (
          <Section
            key={w.id}
            View={View}
            props={props}
            mode={mode}
            handle={handles[w.id]}
            id={w.id}
            name={w.name}
          />
        ) : null,
      )}
    </>
  );
}

function Section<Cfg extends { source?: Source }>({
  View,
  props,
  mode,
  handle,
  id,
  name,
}: {
  View: ComponentType<ViewProps<Cfg>>;
  props: ViewProps<Cfg>;
  mode: "mine" | "all";
  handle: DocHandle;
  id: string;
  name: string;
}) {
  const base = useViewCtx(handle, id);
  const navigate = useNavigate();
  // a task of another workspace opens there, in the list with its panel
  const openTask = useCallback(
    (task: string) => navigate(`/list?workspace=${encodeURIComponent(id)}&task=${encodeURIComponent(task)}`),
    [navigate, id],
  );
  const ctx: ViewCtx | null = useMemo(() => (base ? { ...base, openTask } : null), [base, openTask]);
  const config = useMemo(
    () => ({
      ...props.config,
      source: "here" as const,
      for: mode === "mine" ? "me" : "anyone",
      quickAdd: false,
    }),
    [props.config, mode],
  );
  if (!ctx) return null;
  return (
    <section data-testid="elsewhere" data-workspace={id}>
      <View config={config} ctx={ctx} title={name} widgetId={props.widgetId} quietEmpty />
    </section>
  );
}
